import { type EIP1193Provider } from '@web3-onboard/core'
import { type JsonRpcProvider, StaticJsonRpcProvider, Web3Provider, type Network } from '@ethersproject/providers'
import ExternalStore from '@/services/ExternalStore'
import { EMPTY_DATA } from '@safe-global/protocol-kit/dist/src/utils/constants'
import { type MulticallProvider, MulticallWrapper } from 'ethers-multicall-provider'
import { acquireRpcDispatchSlot, ThrottledBatchProvider, withTransientRpcRetry } from '@/services/rpc/throttledRpc'

/** Calm event polling: nothing in the app needs a faster block heartbeat than the poll cycle. */
const PROVIDER_POLLING_INTERVAL_MS = 15_000

/**
 * Non-batching variant for the multicall wrapper, which does its own request aggregation (and
 * whose debounced flush does not get along with a batching provider underneath: calls hang
 * without ever reaching the network). Every send still goes through the shared dispatch throttle
 * and the transient-error retry. Static base: the network for a given URL never changes here.
 */
export class RetryingJsonRpcProvider extends StaticJsonRpcProvider {
  send(method: string, params: Array<unknown>): Promise<unknown> {
    return withTransientRpcRetry(async () => {
      await acquireRpcDispatchSlot()
      return super.send(method, params)
    })
  }
}

/**
 * Providers are cached per URL: useInitWeb3's effect re-runs on every wallet state emission, and
 * recreating providers each time leaked "zombie" instances whose network-detection and event
 * polling kept consuming the shared dispatch budget forever (a steady 2-3 requests every second),
 * starving interactive calls.
 */
const web3ReadOnlyCache = new Map<string, JsonRpcProvider>()
const multiWeb3ReadOnlyCache = new Map<string, MulticallProvider<JsonRpcProvider>>()

export const createWeb3ReadOnly = (customRpc: string): JsonRpcProvider => {
  const cached = web3ReadOnlyCache.get(customRpc)
  if (cached) return cached

  const provider = new ThrottledBatchProvider({ url: customRpc, timeout: 10_000 })
  provider.pollingInterval = PROVIDER_POLLING_INTERVAL_MS
  web3ReadOnlyCache.set(customRpc, provider)
  return provider
}

export const createMultiWeb3ReadOnly = (customRpc: string, network: Network): MulticallProvider<JsonRpcProvider> => {
  const cacheKey = `${customRpc}_${network.chainId}`
  const cached = multiWeb3ReadOnlyCache.get(cacheKey)
  if (cached) return cached

  const provider = new RetryingJsonRpcProvider(customRpc, network)
  provider.pollingInterval = PROVIDER_POLLING_INTERVAL_MS
  const multiProvider = MulticallWrapper.wrap(provider, 50)
  multiWeb3ReadOnlyCache.set(cacheKey, multiProvider)
  return multiProvider
}

export const createWeb3 = (walletProvider: EIP1193Provider): Web3Provider => {
  return new Web3Provider(walletProvider)
}

export const { setStore: setWeb3, useStore: useWeb3 } = new ExternalStore<Web3Provider>()

export const createSafeAppsWeb3Provider = (safeAppsRpcUri: string): JsonRpcProvider | undefined => {
  return new ThrottledBatchProvider({ url: safeAppsRpcUri, timeout: 10_000 })
}

export const {
  getStore: getWeb3ReadOnly,
  setStore: setWeb3ReadOnly,
  useStore: useWeb3ReadOnly,
} = new ExternalStore<JsonRpcProvider>()

export const {
  getStore: getMultiWeb3ReadOnly,
  setStore: setMultiWeb3ReadOnly,
  useStore: useMultiWeb3ReadOnly,
} = new ExternalStore<MulticallProvider<Web3Provider | JsonRpcProvider>>()

export const getUserNonce = async (userAddress: string): Promise<number> => {
  const web3 = getWeb3ReadOnly()
  if (!web3) return -1
  try {
    return await web3.getTransactionCount(userAddress, 'pending')
  } catch (error) {
    return Promise.reject(error)
  }
}

export const isSmartContract = async (provider: JsonRpcProvider, address: string): Promise<boolean> => {
  const code = await provider.getCode(address)

  return code !== EMPTY_DATA
}
